import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

interface ToastEntry {
  id: number;
  message: string;
}

let nextId = 1;
let pushToast: ((message: string) => void) | null = null;

/**
 * Point d'entrée global pour signaler une erreur de mutation inattendue, utilisable en
 * dehors du rendu React (ex: MutationCache.onError dans main.tsx, où aucun hook n'est
 * disponible). Sans handler monté (avant hydratation ou si le provider est absent),
 * l'appel est silencieusement ignoré plutôt que de planter.
 */
export function reportUnexpectedError(message: string) {
  pushToast?.(message);
}

const TOAST_DURATION_MS = 6000;

export function ErrorToastHost() {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);

  useEffect(() => {
    pushToast = (message: string) => {
      const id = nextId++;
      setToasts((prev) => [...prev, { id, message }]);
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, TOAST_DURATION_MS);
    };
    return () => {
      pushToast = null;
    };
  }, []);

  if (toasts.length === 0) return null;

  return createPortal(
    <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 font-manuscript">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          role="alert"
          className="max-w-sm rounded-xl border border-red-400/30 bg-paper-100 px-4 py-3 text-sm text-manuscript-ink shadow-lg shadow-manuscript-ink/15"
        >
          {toast.message}
        </div>
      ))}
    </div>,
    document.body,
  );
}
